/**
 * @sazeo/sdk — Invoicing hooks
 *
 * TanStack Query wrappers for the invoicing addon. Routes through the Edge
 * Function at `/addons/invoicing/*` which is guarded by requireAddonEnabled()
 * and enforces RLS via the caller's JWT.
 *
 * Paired spec: specs/CODEX_PHASE4_WAVE2_CRM_INVOICING.md §2.4
 */

import type {
  InvCustomer,
  InvCustomerCreateInput,
  InvCustomerFilter,
  InvCustomerUpdateInput,
  InvInvoice,
  InvInvoiceCreateInput,
  InvInvoiceFilter,
  InvInvoiceLine,
  InvInvoiceLineCreateInput,
  InvInvoiceLineUpdateInput,
  InvInvoiceUpdateInput,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const invoicingKeys = {
  all: ['invoicing'] as const,
  customers: (filter?: InvCustomerFilter) =>
    [...invoicingKeys.all, 'customers', filter ?? null] as const,
  customer: (id: string) => [...invoicingKeys.all, 'customer', id] as const,
  invoices: (filter?: InvInvoiceFilter) =>
    [...invoicingKeys.all, 'invoices', filter ?? null] as const,
  invoice: (id: string) => [...invoicingKeys.all, 'invoice', id] as const,
  invoiceLines: (invoiceId: string) =>
    [...invoicingKeys.all, 'invoice', invoiceId, 'lines'] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

function buildQuery(filter?: Record<string, unknown>): string {
  if (!filter) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function useCustomers(supabase: SupabaseClientLike, filter?: InvCustomerFilter) {
  return useQuery({
    queryKey: invoicingKeys.customers(filter),
    queryFn: () =>
      apiFetch<InvCustomer[]>(supabase, `addons/invoicing/customers${buildQuery(filter)}`),
  });
}

export function useCustomer(supabase: SupabaseClientLike, id: string) {
  return useQuery({
    queryKey: invoicingKeys.customer(id),
    queryFn: () => apiFetch<InvCustomer>(supabase, `addons/invoicing/customers/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateCustomer(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvCustomerCreateInput) =>
      apiFetch<InvCustomer>(supabase, 'addons/invoicing/customers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useUpdateCustomer(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: InvCustomerUpdateInput }) =>
      apiFetch<InvCustomer>(supabase, `addons/invoicing/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.customer(vars.id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useDeleteCustomer(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/invoicing/customers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.customer(id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export function useInvoices(supabase: SupabaseClientLike, filter?: InvInvoiceFilter) {
  return useQuery({
    queryKey: invoicingKeys.invoices(filter),
    queryFn: () =>
      apiFetch<InvInvoice[]>(supabase, `addons/invoicing/invoices${buildQuery(filter)}`),
  });
}

export function useInvoice(supabase: SupabaseClientLike, id: string) {
  return useQuery({
    queryKey: invoicingKeys.invoice(id),
    queryFn: () => apiFetch<InvInvoice>(supabase, `addons/invoicing/invoices/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateInvoice(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvInvoiceCreateInput) =>
      apiFetch<InvInvoice>(supabase, 'addons/invoicing/invoices', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useUpdateInvoice(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: InvInvoiceUpdateInput }) =>
      apiFetch<InvInvoice>(supabase, `addons/invoicing/invoices/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(vars.id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useDeleteInvoice(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/invoicing/invoices/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useSendInvoice(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<InvInvoice>(supabase, `addons/invoicing/invoices/${id}/send`, {
        method: 'POST',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

export function useMarkPaid(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paidAt }: { id: string; paidAt?: string }) =>
      apiFetch<InvInvoice>(supabase, `addons/invoicing/invoices/${id}/mark-paid`, {
        method: 'POST',
        body: JSON.stringify(paidAt ? { paid_at: paidAt } : {}),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(vars.id) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Invoice lines
// ---------------------------------------------------------------------------

export function useInvoiceLines(supabase: SupabaseClientLike, invoiceId: string) {
  return useQuery({
    queryKey: invoicingKeys.invoiceLines(invoiceId),
    queryFn: () =>
      apiFetch<InvInvoiceLine[]>(supabase, `addons/invoicing/invoices/${invoiceId}/lines`),
    enabled: Boolean(invoiceId),
  });
}

export function useAddInvoiceLine(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      input,
    }: {
      invoiceId: string;
      input: InvInvoiceLineCreateInput;
    }) =>
      apiFetch<InvInvoiceLine>(supabase, `addons/invoicing/invoices/${invoiceId}/lines`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoiceLines(vars.invoiceId) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(vars.invoiceId) });
    },
  });
}

export function useUpdateInvoiceLine(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      lineId,
      patch,
    }: {
      invoiceId: string;
      lineId: string;
      patch: InvInvoiceLineUpdateInput;
    }) =>
      apiFetch<InvInvoiceLine>(supabase, `addons/invoicing/invoices/${invoiceId}/lines/${lineId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoiceLines(vars.invoiceId) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(vars.invoiceId) });
    },
  });
}

export function useDeleteInvoiceLine(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      lineId,
    }: {
      invoiceId: string;
      lineId: string;
    }) =>
      apiFetch<void>(supabase, `addons/invoicing/invoices/${invoiceId}/lines/${lineId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoiceLines(vars.invoiceId) });
      void qc.invalidateQueries({ queryKey: invoicingKeys.invoice(vars.invoiceId) });
    },
  });
}
